/**
 * Archivo: MensajeriaGlobal.js
 * Autor: Acher Egido Mairal
 * Fecha de última modificación: 2024-07-10
 * 
 * Descripción:
 * Este componente define la vista para la mensajería global. Permite a los usuarios con los permisos adecuados
 * enviar mensajes globales y eliminar mensajes existentes. Los mensajes se listan junto con la fecha de envío.
 * 
 * Dependencias:
 * - react: ^18.2.0
 * - js-cookie: ^3.0.1
 * - Servicios/MensajesServicios: Funciones para interactuar con el servicio de mensajes.
 * - Componentes/MetaTags: Componente para las etiquetas meta del documento.
 * - Componentes/Cabecera: Componente para la cabecera de la aplicación.
 * - Componentes/SegundaCabecera: Componente para la segunda cabecera de la aplicación.
 * 
 * Propósito:
 * Proveer una interfaz de usuario para la gestión de mensajes globales, incluyendo la creación y eliminación de mensajes,
 * así como la visualización de mensajes existentes.
 */

import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import '../Estilos/estilos.css';
import MetaTags from '../Componentes/MetaTags';
import Cabecera from '../Componentes/Cabecera';
import SegundaCabecera from '../Componentes/SegundaCabecera';
import { listaMensajesGlobales, añadirMensajeGlobal, eliminarMensajeGlobal } from '../Servicios/MensajesServicios';

function MensajeriaGlobal() {
  const [mensajesGlobales, setMensajesGlobales] = useState([]);
  const [token, setToken] = useState('');
  const [permisos, setPermisos] = useState(null);
  const [mensajeTexto, setMensajeTexto] = useState('');
  const [mensajeError, setMensajeError] = useState('');
  const [mensajeExito, setMensajeExito] = useState('');

  useEffect(() => {
    const obtenerDatos = async () => {
      const tokenNuevo = Cookies.get('token');
      const permisosNuevos = Cookies.get('permisos');
      setToken(tokenNuevo);
      setPermisos(permisosNuevos);
      await actualizarListaMensajesGlobales(tokenNuevo);
    };
    obtenerDatos();
  }, []);

  const actualizarListaMensajesGlobales = async (token) => {
    const resultado = await listaMensajesGlobales(token);
    if (resultado.success) {
      setMensajesGlobales(resultado.data);
    } else {
      console.log(resultado ? resultado.message : 'Error desconocido');
    }
    //console.log("Mensajes:", mensajesGlobales)
  };

  const manejarEnvioMensaje = async (e) => {
    e.preventDefault();

    if (!mensajeTexto.trim()) {
      setMensajeError('Por favor, escribe un mensaje.');
      return;
    }

    try {
      const result = await añadirMensajeGlobal(token, mensajeTexto.trim());
      if (result.success) {
        setMensajeExito('Mensaje enviado con éxito.');
        setMensajeError('');
        setMensajeTexto('');
        await actualizarListaMensajesGlobales(token);
      } else {
        setMensajeError(result.message);
        setMensajeExito('');
      }
    } catch (error) {
      setMensajeError('Error al enviar el mensaje.');
      setMensajeExito('');
    }
  };

  const handleEliminarMensaje = async (id_mensaje) => {
    try {
      const result = await eliminarMensajeGlobal(token, id_mensaje);
      if (result.success) {
        setMensajeExito('Mensaje eliminado con éxito.');
        setMensajeError('');
        await actualizarListaMensajesGlobales(token);
      } else {
        setMensajeError(result.message);
        setMensajeExito('');
      }
    } catch (error) {
      setMensajeError('Error al eliminar el mensaje.');
      setMensajeExito('');
    }
  };
  
  const renderMensajesGlobales = (mensajes) => {
    if (!mensajes || mensajes.length === 0) {
      return (
        <div className='row-fluid p-2 d-flex justify-content-between'>
          <div>No hay mensajes globales enviados.</div>
        </div>
      );
    }

    return mensajes.map((mensaje, index) => (
      <div key={index} className="row mb-2 align-items-center" style={{ backgroundColor: '#E9ECEF' }}>
        <div className="col-xl-8 col-lg-8 col-md-8 col-6 py-2">
          <span className="text-black">{mensaje.texto_mensaje}</span>
        </div>
        <div className="col-xl-2 col-lg-2 col-md-2 col-3 d-flex justify-content-center">
          <span className="text-black">{new Date(mensaje.fecha_mensaje).toLocaleDateString()}</span>
        </div>
        <div className="col-xl-2 col-lg-2 col-md-2 col-3 d-flex justify-content-end">
          <button 
            className='btn btn-danger btn-sm' 
            onClick={() => handleEliminarMensaje(mensaje.id_mensaje)}
          >
            Eliminar
          </button>
      </div>
      </div>
    ));
  };

  return (
    <>
      <MetaTags />
      <div className="fondo">
        <Cabecera permisos={permisos}/>
        <div className='d-none d-md-block '>
          <SegundaCabecera hayVentanas={false} permisos={permisos} />
          <div className='container-fluid d-flex p-2'>
            <div className='container-fluid col-11'>
              <div className='row-fluid d-flex justify-content-between p-2'>
                <h3 className="text-black">Mensajería Global</h3>
              </div>
              <div className='container-fluid'>
                <div className='row-fluid d-flex flex-column'>
                  <form onSubmit={manejarEnvioMensaje}>
                    <div className="form-group">
                      <label htmlFor="mensajeTexto" className="text-black">Escriba aquí el mensaje:</label>
                      <textarea
                        id="mensajeTexto"
                        className="form-control"
                        rows="3"
                        value={mensajeTexto}
                        onChange={(e) => setMensajeTexto(e.target.value)}
                      />
                    </div>
                    <div className="d-flex justify-content-end">
                      <button type="submit" className='btn text-white' style={{ backgroundColor: 'black', borderRadius: '50px', cursor: 'pointer', padding: '5px 10px' }}>
                        Enviar mensaje
                      </button>
                    </div>
                    {mensajeError && <div className="alert alert-danger mt-3" role="alert">{mensajeError}</div>}
                    {mensajeExito && <div className="alert alert-success mt-3" role="alert">{mensajeExito}</div>}
                  </form>
                  <h5 className="text-black mt-4">Últimos mensajes enviados:</h5>
                  <div className='container-fluid' style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                    {renderMensajesGlobales(mensajesGlobales)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='d-block d-md-none '>
          <div className='container-fluid d-flex p-0'>
            <div className='container-fluid col-12'>
              <div className='row-fluid d-flex justify-content-between p-2'>
                <h3 className="text-black">Mensajería Global</h3>
              </div>
              <div className='container-fluid p-0'>
                <div className='row-fluid d-flex flex-column'>
                  <form onSubmit={manejarEnvioMensaje}>
                    <div className="form-group">
                      <label htmlFor="mensajeTexto" className="text-black">Escriba aquí el mensaje:</label>
                      <textarea
                        id="mensajeTexto"
                        className="form-control"
                        rows="3"
                        value={mensajeTexto}
                        onChange={(e) => setMensajeTexto(e.target.value)}
                      />
                    </div>
                    <div className="d-flex justify-content-end">
                      <button type="submit" className='btn text-white' style={{ backgroundColor: 'black', borderRadius: '50px', cursor: 'pointer', padding: '5px 10px' }}>
                        Enviar mensaje
                      </button>
                    </div>
                    {mensajeError && <div className="alert alert-danger mt-3" role="alert">{mensajeError}</div>}
                    {mensajeExito && <div className="alert alert-success mt-3" role="alert">{mensajeExito}</div>}
                  </form>
                  <h5 className="text-black mt-4">Últimos mensajes enviados:</h5>
                  <div className='container-fluid' style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {renderMensajesGlobales(mensajesGlobales)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default MensajeriaGlobal;
